import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { fadeUp } from "@/lib/motion";
import { track, getUTM } from "@/lib/analytics";

const CONTACT_METHODS = ["Email", "Phone", "WhatsApp"];

export default function QuoteForm({ services = [], closingCta }) {
  const [form, setForm] = useState({
    name: "", business_name: "", email: "", phone: "",
    service: "", description: "", contact_method: "Email",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const [started, setStarted] = useState(false);
  const onStart = () => { if (!started) { setStarted(true); track("quote_start", { section: "quote" }); } };

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading"); setError("");
    try {
      const utm = getUTM();
      await api.post("/quote", {
        ...form, referrer: document.referrer || "",
        session_id: sessionStorage.getItem("neec_sid") || "",
        utm_source: utm.utm_source || "", utm_medium: utm.utm_medium || "", utm_campaign: utm.utm_campaign || "",
      });
      setStatus("success");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setStatus("error");
    }
  };

  const field = "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:border-brand-blue focus:bg-white/10 transition-colors";

  return (
    <section id="quote" data-testid="quote-section" className="relative bg-navy-deep text-white py-28 overflow-hidden noise">
      <div className="absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-brand-blue/20 blur-3xl" />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-start">
        <motion.div {...fadeUp}>
          <span className="eyebrow">Request a Quote</span>
          <h2 className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
            {closingCta || "Ready to take your business digital? Reach out to NEEC Digital today."}
          </h2>
          <p className="mt-6 text-lg text-slate-300 leading-relaxed">
            Tell us a bit about your business and what you want to build. We'll come back to you with a clear plan. No obligation, no pushy sales.
          </p>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-slate-300">
            {["Built to fit you", "Clear communication", "Long-term support"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2 text-sm">
                <CheckCircle2 size={16} className="text-brand-cyan" /> {t}
              </span>
            ))}
          </div>
        </motion.div>

        <motion.div {...fadeUp} className="relative rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur p-6 sm:p-8">
          <AnimatePresence mode="wait">
            {status === "success" ? (
              <motion.div
                key="success"
                data-testid="quote-success"
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center text-center py-14"
              >
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-blue/20 text-brand-cyan">
                  <CheckCircle2 size={34} />
                </div>
                <h3 className="mt-6 font-display text-2xl font-bold">Request received.</h3>
                <p className="mt-3 text-slate-300 max-w-sm">
                  Thanks {form.name.split(" ")[0] || "there"}, your request has reached the NEEC Digital team. We'll be in touch by {form.contact_method.toLowerCase()} soon.
                </p>
                <button
                  onClick={() => { setForm({ name: "", business_name: "", email: "", phone: "", service: "", description: "", contact_method: "Email" }); setStatus("idle"); }}
                  className="mt-8 rounded-full border border-white/20 px-6 py-2.5 text-sm font-semibold hover:bg-white/10 transition-colors"
                >
                  Send another request
                </button>
              </motion.div>
            ) : (
              <motion.form key="form" onSubmit={submit} onFocus={onStart} data-testid="quote-form" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-400 mb-1.5 block">Name *</label>
                  <input required data-testid="quote-form-name-input" value={form.name} onChange={set("name")} placeholder="Your name" className={field} />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1.5 block">Business name</label>
                  <input data-testid="quote-form-business-input" value={form.business_name} onChange={set("business_name")} placeholder="Your business" className={field} />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1.5 block">Email *</label>
                  <input required type="email" data-testid="quote-form-email-input" value={form.email} onChange={set("email")} placeholder="you@company.com" className={field} />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1.5 block">Phone (optional)</label>
                  <input data-testid="quote-form-phone-input" value={form.phone} onChange={set("phone")} placeholder="+1 …" className={field} />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-slate-400 mb-1.5 block">Service interested in</label>
                  <select data-testid="quote-form-service-select" value={form.service} onChange={set("service")} className={`${field} appearance-none`}>
                    <option value="" className="bg-navy">Select a service…</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.title} className="bg-navy">{s.title}</option>
                    ))}
                    <option value="Not sure yet" className="bg-navy">Not sure yet</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-slate-400 mb-1.5 block">Brief project description *</label>
                  <textarea required data-testid="quote-form-description-input" value={form.description} onChange={set("description")} rows={4} placeholder="Tell us what you're trying to build or improve…" className={`${field} resize-none`} />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-slate-400 mb-2 block">Preferred contact method</label>
                  <div className="flex gap-2" data-testid="quote-form-contact-method">
                    {CONTACT_METHODS.map((m) => (
                      <button key={m} type="button" onClick={() => setForm((f) => ({ ...f, contact_method: m }))}
                        className={`flex-1 rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors ${
                          form.contact_method === m ? "border-brand-blue bg-brand-blue text-white" : "border-white/15 text-slate-300 hover:bg-white/5"
                        }`}>
                        {m}
                      </button>
                    ))}
                  </div>
                </div>

                {status === "error" && (
                  <p data-testid="quote-form-error" className="sm:col-span-2 text-sm text-red-400">{error}</p>
                )}

                <button
                  type="submit"
                  data-testid="quote-form-submit-button"
                  disabled={status === "loading"}
                  className="sm:col-span-2 group inline-flex items-center justify-center gap-2 rounded-xl bg-brand-blue px-6 py-3.5 text-base font-semibold text-white shadow-xl shadow-brand-blue/25 hover:bg-blue-500 transition-all active:scale-[0.99] disabled:opacity-60"
                >
                  {status === "loading" ? (<><Loader2 size={18} className="animate-spin" /> Sending…</>) : (<>Request a Quote <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" /></>)}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
